import { useConfirmDialog } from '../hooks/useConfirmDialog';
/**
 * DeliveryRoutesPage - Gestione Percorsi di Consegna
 * 
 * Layout "Command Center": Mappa fullscreen + Sidebar flottante glassmorphism
 */
import React, { useState, useEffect, lazy, Suspense } from 'react';
import { getErrorMessage } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useExtensions } from '../hooks/useExtensions';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogDescription
} from '../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Route,
  Plus,
  Calendar,
  Truck,
  MapPin,
  User,
  Play,
  CheckCircle2,
  Clock,
  Package,
  ChevronRight,
  ChevronLeft,
  Trash2,
  Navigation,
  Loader2,
  AlertCircle,
  Map,
  PanelLeftClose,
  PanelLeft,
  Sparkles,
  CircleDot,
  Target
} from 'lucide-react';

const RouteMap = lazy(() => import('../components/RouteMap'));

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status colors and labels
const STATUS_CONFIG = {
  draft: { label: 'Bozza', color: 'bg-slate-400', textColor: 'text-slate-600', borderColor: 'border-l-slate-400' },
  planned: { label: 'Pianificato', color: 'bg-blue-400', textColor: 'text-blue-600', borderColor: 'border-l-blue-400' },
  in_progress: { label: 'In Corso', color: 'bg-amber-500', textColor: 'text-amber-600', borderColor: 'border-l-amber-500' },
  completed: { label: 'Completato', color: 'bg-emerald-500', textColor: 'text-emerald-600', borderColor: 'border-l-emerald-500' }
};

// Route Card Component
const RouteCard = ({ route, isSelected, onClick }) => {
  const status = STATUS_CONFIG[route.status] || STATUS_CONFIG.draft;
  const completedStops = route.completed_stops || 0;
  const totalStops = route.total_orders || 0;
  const progress = totalStops > 0 ? (completedStops / totalStops) * 100 : 0;
  
  return (
    <div
      data-testid={`route-card-${route.id}`}
      onClick={onClick}
      className={`
        relative p-4 rounded-xl cursor-pointer transition-all duration-200
        border-l-4 ${status.borderColor}
        ${isSelected 
          ? 'bg-primary/10 ring-2 ring-primary shadow-lg scale-[1.02]' 
          : 'bg-card hover:bg-muted/50 hover:shadow-md hover:scale-[1.01]'
        }
      `}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm truncate">{route.name || `Percorso ${route.date}`}</h3>
          <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
            <Calendar className="h-3 w-3 shrink-0" />
            <span>{route.date}</span>
          </div>
        </div>
        <Badge 
          variant="secondary" 
          className={`shrink-0 text-[10px] px-2 py-0.5 ${status.color} text-white`}
        >
          {status.label}
        </Badge>
      </div>
      
      {/* Progress Bar */}
      {route.status === 'in_progress' && (
        <div className="mb-3">
          <Progress value={progress} className="h-1.5" />
          <p className="text-[10px] text-muted-foreground mt-1">
            {completedStops}/{totalStops} fermate completate
          </p>
        </div>
      )}
      
      {/* Footer Stats */}
      <div className="flex items-center justify-between text-xs">
        <div className="flex items-center gap-3 text-muted-foreground">
          <span className="flex items-center gap-1">
            <Package className="h-3 w-3" />
            {route.total_orders}
          </span>
          {route.total_distance_km && (
            <span className="flex items-center gap-1">
              <Route className="h-3 w-3" />
              {route.total_distance_km} km
            </span>
          )}
        </div>
        {route.driver_name && (
          <span className="flex items-center gap-1 text-muted-foreground">
            <User className="h-3 w-3" />
            <span className="truncate max-w-[80px]">{route.driver_name}</span>
          </span>
        )}
      </div>
    </div>
  );
};

// Stop Timeline Item
const StopTimelineItem = ({ stop, index, isLast, isActive, onStatusChange }) => {
  const isCompleted = stop.status === 'completed';
  const isArrived = stop.status === 'arrived';
  
  return (
    <div className="flex gap-3" data-testid={`stop-${stop.order_id}`}>
      {/* Timeline Indicator */}
      <div className="flex flex-col items-center">
        <div className={`
          w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold
          transition-all duration-300
          ${isCompleted 
            ? 'bg-emerald-500 text-white' 
            : isArrived 
              ? 'bg-amber-500 text-white animate-pulse' 
              : 'bg-muted text-muted-foreground border-2 border-dashed border-muted-foreground/30'
          }
        `}>
          {isCompleted ? <CheckCircle2 className="h-4 w-4" /> : index + 1}
        </div>
        {!isLast && (
          <div className={`w-0.5 h-full min-h-[40px] -my-1 ${isCompleted ? 'bg-emerald-500' : 'bg-border'}`} />
        )}
      </div>
      
      {/* Content */}
      <div className={`flex-1 pb-4 ${isCompleted ? 'opacity-60' : ''}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className={`font-medium text-sm ${isCompleted ? 'line-through' : ''}`}>
              {stop.order_info?.site?.name || `Ordine #${stop.order_info?.order_number}`}
            </p>
            {stop.order_info?.site && (
              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                <MapPin className="h-3 w-3 shrink-0" />
                <span className="truncate">{stop.order_info.site.address}, {stop.order_info.site.city}</span>
              </p>
            )}
            {stop.order_info?.site?.contact_phone && (
              <p className="text-xs text-muted-foreground mt-0.5">
                📞 {stop.order_info.site.contact_phone}
              </p>
            )}
          </div>
          
          {/* Action Buttons */}
          {isActive && !isCompleted && (
            <div className="flex items-center gap-1 shrink-0 ml-2">
              {!isArrived ? (
                <>
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="h-7 text-xs px-2"
                    onClick={() => onStatusChange(stop.order_id, 'arrived')}
                    title="Segna come arrivato"
                  >
                    <Navigation className="h-3.5 w-3.5" />
                  </Button>
                  <Button 
                    size="sm"
                    className="h-7 text-xs px-2 bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => onStatusChange(stop.order_id, 'completed')}
                    title="Segna come completato"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  </Button>
                </>
              ) : (
                <Button 
                  size="sm"
                  className="h-7 text-xs px-3 bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => onStatusChange(stop.order_id, 'completed')}
                >
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                  Fatto
                </Button>
              )}
            </div>
          )}
          
          {isCompleted && (
            <Badge variant="secondary" className="bg-emerald-100 text-emerald-700 text-[10px] shrink-0 ml-2">
              ✓ Consegnato
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
};

const DeliveryRoutesPage = () => {
  const { confirm: confirmAction, ConfirmDialog } = useConfirmDialog();
  const { user } = useAuth();
  const { isFeatureActive } = useExtensions();
  const navigate = useNavigate();
  
  const [routes, setRoutes] = useState([]);
  const [todayRoutes, setTodayRoutes] = useState([]);
  const [availableOrders, setAvailableOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('today');
  
  // Dialog nuovo percorso
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createForm, setCreateForm] = useState({
    date: new Date().toISOString().split('T')[0],
    name: '',
    driver_name: '',
    vehicle_info: '',
    start_address: ''
  });
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [creating, setCreating] = useState(false);
  
  // Percorso selezionato
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [optimizing, setOptimizing] = useState(false);
  
  // Mobile sheet
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);

  const hasRoutesFeature = isFeatureActive('ext_logistics', 'routes');

  useEffect(() => {
    if (user?.role === 'seller' && hasRoutesFeature) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [user, hasRoutesFeature]);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchTodayRoutes(),
        fetchRoutes(),
        fetchAvailableOrders()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTodayRoutes = async () => {
    try {
      const res = await axios.get(`${API}/delivery-routes/today`);
      setTodayRoutes(res.data.routes || []);
    } catch (error) {
      console.error('Error fetching today routes:', error);
    }
  };

  const fetchRoutes = async () => {
    try {
      const res = await axios.get(`${API}/delivery-routes`);
      setRoutes(res.data.routes || []);
    } catch (error) {
      console.error('Error fetching routes:', error);
    }
  };

  const fetchAvailableOrders = async () => {
    try {
      const res = await axios.get(`${API}/delivery-routes/orders-to-deliver`);
      setAvailableOrders(res.data.orders || []);
    } catch (error) {
      console.error('Error fetching available orders:', error);
    }
  };

  const fetchRouteDetail = async (routeId) => {
    try {
      const res = await axios.get(`${API}/delivery-routes/${routeId}`);
      setSelectedRoute(res.data);
    } catch (error) {
      toast.error('Errore nel caricamento del percorso');
    }
  };

  const handleCreateRoute = async () => {
    if (selectedOrders.length === 0) {
      toast.error('Seleziona almeno un ordine');
      return;
    }

    setCreating(true);
    try {
      const res = await axios.post(`${API}/delivery-routes`, {
        ...createForm,
        order_ids: selectedOrders
      });
      toast.success('🚚 Percorso creato con successo!');
      setShowCreateDialog(false);
      setSelectedOrders([]);
      setCreateForm({
        date: new Date().toISOString().split('T')[0],
        name: '',
        driver_name: '',
        vehicle_info: '',
        start_address: ''
      });
      fetchData();
      // Auto-select the new route
      if (res.data?.id) {
        fetchRouteDetail(res.data.id);
      }
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Errore nella creazione'));
    } finally {
      setCreating(false);
    }
  };

  const handleStartRoute = async (routeId) => {
    try {
      await axios.post(`${API}/delivery-routes/${routeId}/start`);
      toast.success('🏁 Percorso avviato! Buona consegna!');
      fetchData();
      fetchRouteDetail(routeId);
    } catch (error) {
      toast.error('Errore nell\'avvio del percorso');
    }
  };

  const handleCompleteRoute = async (routeId) => {
    try {
      await axios.post(`${API}/delivery-routes/${routeId}/complete`);
      toast.success('🎉 Percorso completato!');
      fetchData();
      setSelectedRoute(null);
    } catch (error) {
      toast.error('Errore nel completamento');
    }
  };

  const handleUpdateStopStatus = async (orderId, status) => {
    if (!selectedRoute) return;
    try {
      await axios.post(`${API}/delivery-routes/${selectedRoute.id}/stops/${orderId}/status`, { status });
      toast.success(status === 'completed' ? '✓ Consegna completata!' : '📍 Arrivato alla fermata');
      fetchRouteDetail(selectedRoute.id);
      fetchData();
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
    }
  };

  const handleOptimizeRoute = async (routeId) => {
    setOptimizing(true);
    try {
      const res = await axios.post(`${API}/delivery-routes/${routeId}/optimize`);
      const opt = res.data.optimization;
      toast.success(`✨ Percorso ottimizzato! Risparmiati ${opt.savings_km} km (${opt.savings_percent}%)`);
      fetchData();
      fetchRouteDetail(routeId);
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Errore nell\'ottimizzazione'));
    } finally {
      setOptimizing(false);
    }
  };

  const handleDeleteRoute = async (routeId) => {
    if (!await confirmAction({ title: 'Elimina percorso', description: 'Eliminare questo percorso?', confirmText: 'Elimina', variant: 'destructive' })) return;
    try {
      await axios.delete(`${API}/delivery-routes/${routeId}`);
      toast.success('Percorso eliminato');
      fetchData();
      if (selectedRoute?.id === routeId) setSelectedRoute(null);
    } catch (error) {
      toast.error('Solo le bozze possono essere eliminate');
    }
  };

  const toggleOrderSelection = (orderId) => {
    setSelectedOrders(prev => 
      prev.includes(orderId) ? prev.filter(id => id !== orderId) : [...prev, orderId]
    );
  };

  // Access control
  if (user?.role !== 'seller') {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
          <p className="text-muted-foreground">Solo i venditori possono gestire i percorsi</p>
        </div>
      </div>
    );
  }

  if (!hasRoutesFeature) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center max-w-md">
          <Route className="h-20 w-20 mx-auto text-muted-foreground/30 mb-6" />
          <h2 className="text-xl font-semibold mb-2">Percorsi di Consegna</h2>
          <p className="text-muted-foreground mb-6">
            Attiva il modulo "Ottimizzazione Percorsi" per pianificare i tuoi giri di consegna.
          </p>
          <Button onClick={() => navigate('/settings')}>
            Vai alle Impostazioni
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
      </div>
    );
  }

  // Sidebar content (reusable for desktop and mobile)
  const SidebarContent = ({ showCloseButton = false }: { showCloseButton?: boolean }) => (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-border/50">
        <div className="flex items-center justify-between gap-2 mb-4">
          <h1 className="text-base font-bold flex items-center gap-2 shrink-0">
            <Truck className="h-5 w-5 text-primary" />
            Consegne
          </h1>
          <div className="flex items-center gap-2">
            <Button 
              size="sm" 
              onClick={() => setShowCreateDialog(true)}
              data-testid="new-route-btn"
              className="h-8 px-3"
            >
              <Plus className="h-4 w-4 mr-1" />
              Nuovo
            </Button>
            {showCloseButton && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 shrink-0"
                onClick={() => setSidebarOpen(false)}
              >
                <PanelLeftClose className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        
        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-muted/50 rounded-lg p-2 text-center">
            <p className="text-lg font-bold text-primary">{todayRoutes.length}</p>
            <p className="text-[10px] text-muted-foreground">Oggi</p>
          </div>
          <div className="bg-muted/50 rounded-lg p-2 text-center">
            <p className="text-lg font-bold text-amber-500">
              {todayRoutes.filter(r => r.status === 'in_progress').length}
            </p>
            <p className="text-[10px] text-muted-foreground">In Corso</p>
          </div>
          <div className="bg-muted/50 rounded-lg p-2 text-center">
            <p className="text-lg font-bold text-emerald-500">{availableOrders.length}</p>
            <p className="text-[10px] text-muted-foreground">Da Cons.</p>
          </div>
        </div>
      </div>
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
        <TabsList className="mx-4 mt-3 grid grid-cols-2">
          <TabsTrigger value="today" className="text-xs">
            <Calendar className="h-3 w-3 mr-1" />
            Oggi
          </TabsTrigger>
          <TabsTrigger value="all" className="text-xs">
            <Route className="h-3 w-3 mr-1" />
            Tutti
          </TabsTrigger>
        </TabsList>
        
        <ScrollArea className="flex-1 px-4 py-3">
          <TabsContent value="today" className="mt-0 space-y-2">
            {todayRoutes.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Calendar className="h-10 w-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Nessun percorso per oggi</p>
                <Button 
                  variant="link" 
                  size="sm" 
                  onClick={() => setShowCreateDialog(true)}
                  className="mt-2"
                >
                  Crea il primo percorso
                </Button>
              </div>
            ) : (
              todayRoutes.map(route => (
                <RouteCard
                  key={route.id}
                  route={route}
                  isSelected={selectedRoute?.id === route.id}
                  onClick={() => fetchRouteDetail(route.id)}
                />
              ))
            )}
          </TabsContent>
          
          <TabsContent value="all" className="mt-0 space-y-2">
            {routes.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Route className="h-10 w-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Nessun percorso creato</p>
              </div>
            ) : (
              routes.slice(0, 15).map(route => (
                <RouteCard
                  key={route.id}
                  route={route}
                  isSelected={selectedRoute?.id === route.id}
                  onClick={() => fetchRouteDetail(route.id)}
                />
              ))
            )}
          </TabsContent>
        </ScrollArea>
      </Tabs>
    </div>
  );

  return (
    <div className="relative h-[calc(100vh-8rem)] -m-4 lg:-m-8 overflow-hidden" data-testid="delivery-routes-page">
      {/* Fullscreen Map Background */}
      <div className="absolute inset-0 bg-muted/30 z-0">
        <Suspense fallback={
          <div className="h-full flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        }>
          <RouteMap 
            stops={selectedRoute?.stops || []}
            startPoint={selectedRoute?.start_address ? {
              lat: selectedRoute.start_lat,
              lng: selectedRoute.start_lng,
              address: selectedRoute.start_address
            } : null}
            height="100%"
            onStopClick={(stop) => {
              document.getElementById(`stop-${stop.order_id}`)?.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'center' 
              });
            }}
          />
        </Suspense>
        
        {/* Empty State Overlay */}
        {!selectedRoute && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/60 backdrop-blur-sm z-10">
            <div className="text-center">
              <Target className="h-16 w-16 mx-auto text-muted-foreground/40 mb-4" />
              <p className="text-muted-foreground font-medium">
                Seleziona un percorso per visualizzarlo sulla mappa
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Desktop Floating Sidebar */}
      <div 
        className={`
          hidden lg:flex absolute left-4 top-4 bottom-4 z-30
          transition-all duration-300 ease-out
          ${sidebarOpen ? 'w-[360px]' : 'w-14'}
        `}
      >
        <div className="h-full w-full bg-background/95 backdrop-blur-xl rounded-2xl border border-border/50 shadow-2xl overflow-hidden">
          {sidebarOpen ? (
            <SidebarContent showCloseButton={true} />
          ) : (
            <div className="h-full flex flex-col items-center py-4 gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-10 w-10 p-0"
                onClick={() => setSidebarOpen(true)}
              >
                <PanelLeft className="h-5 w-5" />
              </Button>
              <div className="flex-1" />
              <Button
                size="sm"
                className="h-10 w-10 p-0"
                onClick={() => {
                  setSidebarOpen(true);
                  setShowCreateDialog(true);
                }}
              >
                <Plus className="h-5 w-5" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Route Detail Panel (Right side) */}
      {selectedRoute && (
        <div className="hidden lg:flex absolute right-4 top-4 bottom-4 w-[420px] z-30">
          <div className="h-full w-full bg-background/95 backdrop-blur-xl rounded-2xl border border-border/50 shadow-2xl overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-border/50">
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="flex-1 min-w-0">
                  <h2 className="font-bold text-lg truncate">{selectedRoute.name}</h2>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {selectedRoute.date}
                    </span>
                    {selectedRoute.driver_name && (
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {selectedRoute.driver_name}
                      </span>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 shrink-0"
                  onClick={() => setSelectedRoute(null)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              
              {/* Stats Bar */}
              {selectedRoute.total_distance_km && (
                <div className="flex items-center gap-4 text-xs bg-muted/50 rounded-lg p-2">
                  <div className="flex items-center gap-1">
                    <Route className="h-3.5 w-3.5 text-primary" />
                    <span className="font-medium">{selectedRoute.total_distance_km} km</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5 text-primary" />
                    <span className="font-medium">
                      ~{Math.floor((selectedRoute.total_duration_minutes || 0) / 60)}h {(selectedRoute.total_duration_minutes || 0) % 60}m
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Package className="h-3.5 w-3.5 text-primary" />
                    <span className="font-medium">{selectedRoute.stops?.length || 0} fermate</span>
                  </div>
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="flex items-center gap-2 mt-3">
                {selectedRoute.status === 'draft' && (
                  <>
                    <Button 
                      size="sm" 
                      className="flex-1"
                      onClick={() => handleStartRoute(selectedRoute.id)}
                    >
                      <Play className="h-4 w-4 mr-1" />
                      Avvia Giro
                    </Button>
                    {selectedRoute.stops?.length >= 2 && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleOptimizeRoute(selectedRoute.id)}
                        disabled={optimizing}
                      >
                        {optimizing ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Sparkles className="h-4 w-4" />
                        )}
                      </Button>
                    )}
                    <Button 
                      size="sm" 
                      variant="destructive"
                      className="px-2"
                      onClick={() => handleDeleteRoute(selectedRoute.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </>
                )}
                
                {selectedRoute.status === 'in_progress' && (
                  <Button 
                    size="sm" 
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => handleCompleteRoute(selectedRoute.id)}
                  >
                    <CheckCircle2 className="h-4 w-4 mr-1" />
                    Completa Giro
                  </Button>
                )}
                
                {selectedRoute.status === 'completed' && (
                  <Badge className="flex-1 justify-center py-2 bg-emerald-600">
                    <CheckCircle2 className="h-4 w-4 mr-1" />
                    Completato
                  </Badge>
                )}
              </div>
            </div>
            
            {/* Stops Timeline */}
            <ScrollArea className="flex-1 p-4">
              <div className="space-y-0">
                {selectedRoute.stops?.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <MapPin className="h-10 w-10 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">Nessuna fermata</p>
                  </div>
                ) : (
                  selectedRoute.stops?.map((stop, index) => (
                    <StopTimelineItem
                      key={stop.order_id}
                      stop={stop}
                      index={index}
                      isLast={index === selectedRoute.stops.length - 1}
                      isActive={selectedRoute.status === 'in_progress'}
                      onStatusChange={handleUpdateStopStatus}
                    />
                  ))
                )}
              </div>
            </ScrollArea>
          </div>
        </div>
      )}

      {/* Mobile Bottom Bar */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-30 p-4 bg-background/95 backdrop-blur-xl border-t border-border/50">
        <div className="flex items-center gap-3">
          <Button 
            className="flex-1"
            onClick={() => setMobileSheetOpen(true)}
          >
            <Route className="h-4 w-4 mr-2" />
            Percorsi ({todayRoutes.length})
          </Button>
          <Button 
            size="icon"
            onClick={() => setShowCreateDialog(true)}
          >
            <Plus className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Mobile Sheet */}
      <Sheet open={mobileSheetOpen} onOpenChange={setMobileSheetOpen}>
        <SheetContent side="bottom" className="h-[80vh] rounded-t-2xl">
          <SheetHeader className="sr-only">
            <SheetTitle>Percorsi di Consegna</SheetTitle>
          </SheetHeader>
          <SidebarContent />
        </SheetContent>
      </Sheet>

      {/* Create Route Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5 text-primary" />
              Nuovo Percorso
            </DialogTitle>
            <DialogDescription>
              Crea un nuovo percorso di consegna selezionando gli ordini da includere.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            {/* Form Fields */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Data</Label>
                <Input
                  type="date"
                  value={createForm.date}
                  onChange={(e) => setCreateForm({ ...createForm, date: e.target.value })}
                  className="h-9"
                />
              </div>
              <div>
                <Label className="text-xs">Nome</Label>
                <Input
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  placeholder="Es: Giro Nord"
                  className="h-9"
                />
              </div>
              <div>
                <Label className="text-xs">Autista</Label>
                <Input
                  value={createForm.driver_name}
                  onChange={(e) => setCreateForm({ ...createForm, driver_name: e.target.value })}
                  placeholder="Nome"
                  className="h-9"
                />
              </div>
              <div>
                <Label className="text-xs">Veicolo</Label>
                <Input
                  value={createForm.vehicle_info}
                  onChange={(e) => setCreateForm({ ...createForm, vehicle_info: e.target.value })}
                  placeholder="Targa"
                  className="h-9"
                />
              </div>
            </div>

            {/* Order Selection */}
            <div>
              <Label className="text-xs mb-2 block">
                Ordini da consegnare ({selectedOrders.length} selezionati)
              </Label>
              
              {availableOrders.length === 0 ? (
                <div className="p-6 text-center border rounded-lg bg-muted/30">
                  <Package className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />
                  <p className="text-sm text-muted-foreground">Nessun ordine disponibile</p>
                </div>
              ) : (
                <ScrollArea className="h-[200px] border rounded-lg">
                  <div className="p-2 space-y-1">
                    {availableOrders.map(order => (
                      <div
                        key={order.order_id}
                        className={`
                          p-2.5 rounded-lg cursor-pointer transition-all text-sm
                          ${selectedOrders.includes(order.order_id) 
                            ? 'bg-primary/10 border border-primary' 
                            : 'hover:bg-muted/50 border border-transparent'
                          }
                        `}
                        onClick={() => toggleOrderSelection(order.order_id)}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`
                            w-4 h-4 rounded border-2 flex items-center justify-center shrink-0
                            ${selectedOrders.includes(order.order_id)
                              ? 'border-primary bg-primary text-white'
                              : 'border-muted-foreground/30'
                            }
                          `}>
                            {selectedOrders.includes(order.order_id) && (
                              <CheckCircle2 className="h-2.5 w-2.5" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">
                              #{order.order_number} - {order.site?.name || 'Cliente'}
                            </p>
                            {order.site && (
                              <p className="text-xs text-muted-foreground truncate">
                                {order.site.address}, {order.site.city}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleCreateRoute}
              disabled={creating || selectedOrders.length === 0}
            >
              {creating ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              Crea Percorso
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DeliveryRoutesPage;
